import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import JsonLd, { articleSchema } from '../components/JsonLd';

const Example = ({ children }) => (
  <div className="sf-example">
    <div className="sf-example-label">Real-world example</div>
    <p>{children}</p>
  </div>
);

const StockMarketFundamentals = () => {
  return (
    <>
      <SEO
        title="Stock Market Fundamentals - How the Stock Market Works | StckFuture"
        description="Learn how the stock market works, understand market participants, stock exchanges, IPOs, market cycles, and market capitalization. Free beginner's guide."
        canonical="https://www.stckfuture.com/stock-market-fundamentals"
      />
      <JsonLd schema={articleSchema({
        title: 'Stock Market Fundamentals - How the Stock Market Works | StckFuture',
        description: 'Learn how the stock market works, understand market participants, stock exchanges, IPOs, market cycles, and market capitalization.',
        url: 'https://www.stckfuture.com/stock-market-fundamentals',
        dateModified: '2026-06-13',
      })} />
      <div className="sf-content-page">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto">

              <div className="sf-page-header">
                <span className="sf-eyebrow">Module 1</span>
                <h1>Stock Market Fundamentals</h1>
                <p className="lead">Understand what the stock market is, who participates in it, and how prices are set — the foundation everything else builds on.</p>
              </div>

              {/* What is the Stock Market */}
              <div className="sf-card">
                <h3>What is the stock market?</h3>
                <p>The stock market is a collection of markets where stocks — pieces of ownership in businesses — are traded between investors. Think of it as a giant marketplace where instead of buying groceries, people buy and sell small pieces of companies.</p>
                <p>When you buy a stock, you're buying a share of that company and becoming a shareholder. If the company grows and becomes more valuable, your share is worth more.</p>
                <Example>When Apple went public in 1980, its IPO price was $22 per share. If you had invested $1,000 (about 45 shares) and held until today, your investment would be worth over $1.5 million — demonstrating how owning a small piece of a successful company can create significant wealth over time.</Example>
              </div>

              {/* Market Participants */}
              <div className="sf-card">
                <h3>Market participants</h3>
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <strong>Individual investors</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Regular people who buy and sell stocks for personal investment. They make up about 25% of market activity and trade through platforms like Robinhood, E*TRADE, or Charles Schwab.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Institutional investors</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Large organizations like pension funds, mutual funds, and insurance companies. They manage trillions of dollars and account for roughly 75% of trading volume.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Market makers</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Firms like Citadel Securities that provide liquidity by always being ready to buy or sell, ensuring you can trade instantly at competitive prices.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Brokers</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Intermediaries who execute buy and sell orders. Modern brokers also provide research tools, educational resources, and sometimes financial advice.</p>
                  </div>
                </div>
                <Example>In January 2021, individual investors coordinating on Reddit's WallStreetBets drove GameStop (GME) from $17 to nearly $350 per share — demonstrating that even retail investors, acting collectively, can move markets traditionally dominated by institutions.</Example>
              </div>

              {/* Stock Exchanges */}
              <div className="sf-card">
                <h3>Stock exchanges</h3>
                <p>Stock exchanges are organized markets where stocks are listed and traded. The most important ones:</p>
                <ul>
                  <li className="mb-2"><strong>NYSE (New York Stock Exchange)</strong> — the largest in the world, listing 2,300+ companies including Walmart, Coca-Cola, and Disney.</li>
                  <li className="mb-2"><strong>NASDAQ</strong> — the first electronic exchange (1971), home to most major tech companies including Apple, Microsoft, and Google.</li>
                  <li className="mb-2"><strong>LSE (London Stock Exchange)</strong> — Europe's largest, listing companies from over 60 countries.</li>
                  <li className="mb-2"><strong>TSE (Tokyo Stock Exchange)</strong> — the largest in Asia, home to Toyota, Sony, and SoftBank.</li>
                </ul>
                <Example>When Facebook (now Meta) went public in 2012, it chose NASDAQ over NYSE and priced its IPO at $38/share — valuing the company at $104 billion, the largest valuation for a newly public company at the time.</Example>
              </div>

              {/* How Prices Are Determined */}
              <div className="sf-card">
                <h3>How stock prices are determined</h3>
                <p>Prices are set by supply and demand. When more people want to buy a stock than sell it, the price rises. When more want to sell, it falls. Several factors drive those decisions:</p>
                <ul>
                  <li className="mb-1"><strong>Company performance</strong> — earnings reports, revenue growth, profit margins</li>
                  <li className="mb-1"><strong>Industry trends</strong> — how the broader sector is performing</li>
                  <li className="mb-1"><strong>Economic indicators</strong> — interest rates, inflation, GDP</li>
                  <li className="mb-1"><strong>Market sentiment</strong> — investor confidence and emotional reactions</li>
                </ul>
                <Example>In February 2023, Meta reported better-than-expected earnings and announced a $40 billion buyback. The stock jumped 23% in a single day, adding over $100 billion to its market value — illustrating how company performance directly moves prices through supply and demand.</Example>
              </div>

              {/* IPOs */}
              <div className="sf-card">
                <h3>Initial public offerings (IPOs)</h3>
                <p>An IPO is when a private company offers shares to the public for the first time. The process: hire investment banks → file financial statements with regulators → roadshow to generate investor interest → set price → begin trading.</p>
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <strong>Advantages</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Access to capital, increased visibility, liquidity for early investors and employees.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Disadvantages</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Expensive process, increased regulatory requirements, pressure to hit short-term targets every quarter.</p>
                  </div>
                </div>
                <Example>When Airbnb went public in December 2020, it priced shares at $68 and opened at $146 on its first trading day — demonstrating how unpredictable IPO pricing can be and how strong investor demand can push a stock far above its listing price immediately.</Example>
              </div>

              {/* Indices */}
              <div className="sf-card">
                <h3>Stock market indices</h3>
                <p>An index tracks the combined performance of a group of stocks, giving you a snapshot of how a segment of the market is doing. They serve as benchmarks investors compare their own returns against.</p>
                <ul>
                  <li className="mb-2"><strong>S&P 500</strong> — 500 large U.S. companies, market-cap weighted. Considered the best single measure of the U.S. market.</li>
                  <li className="mb-2"><strong>Dow Jones Industrial Average</strong> — 30 prominent companies, price-weighted. Oldest and most quoted, but less representative due to its small size.</li>
                  <li className="mb-2"><strong>NASDAQ Composite</strong> — technology-heavy index of 3,000+ companies.</li>
                  <li className="mb-2"><strong>Russell 2000</strong> — 2,000 small-cap U.S. companies.</li>
                </ul>
                <Example>In 2020, the S&P 500 gained about 16% while the tech-heavy NASDAQ soared ~43%. The same economic environment produced dramatically different results — showing why comparing your portfolio to the right benchmark matters.</Example>
              </div>

              {/* Market Cycles */}
              <div className="sf-card">
                <h3>Market cycles</h3>
                <p>Markets move in cycles between expansion (bull markets) and contraction (bear markets). The four phases:</p>
                <ol>
                  <li className="mb-2"><strong>Accumulation</strong> — smart money begins buying after a bottom while sentiment is still negative</li>
                  <li className="mb-2"><strong>Mark-up</strong> — prices trend upward as more investors enter and confidence grows</li>
                  <li className="mb-2"><strong>Distribution</strong> — early investors begin selling to take profits near the top</li>
                  <li className="mb-2"><strong>Mark-down</strong> — prices trend downward as selling accelerates</li>
                </ol>
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <strong>Bull market</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Prices rising 20%+ from recent lows. Strong economic indicators, high confidence. Typically lasts 2–5 years.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Bear market</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Prices falling 20%+ from recent highs. Economic slowdown, investor pessimism. Typically lasts 9–16 months.</p>
                  </div>
                </div>
                <Example>The bull market after the 2008 crisis ran nearly 11 years (March 2009–February 2020) — the longest in U.S. history — with the S&P 500 gaining 400%+. It ended in weeks when COVID-19 hit, with markets falling 34%. The speed of that reversal showed how quickly external shocks can end even the longest bull runs.</Example>
              </div>

              {/* Market Cap */}
              <div className="sf-card">
                <h3>Market capitalisation</h3>
                <p>Market cap = share price × shares outstanding. It's the most common way to categorise companies by size.</p>
                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <strong>Large-cap</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Over $10 billion. Generally stable, often pay dividends. Examples: Apple, Microsoft, Amazon.</p>
                  </div>
                  <div className="col-md-4">
                    <strong>Mid-cap</strong>
                    <p className="sf-text-small sf-text-muted mt-1">$2–10 billion. Balance of stability and growth potential. Often in expansion phase.</p>
                  </div>
                  <div className="col-md-4">
                    <strong>Small-cap</strong>
                    <p className="sf-text-small sf-text-muted mt-1">$300 million–$2 billion. Higher growth potential but more volatile. Less analyst coverage.</p>
                  </div>
                </div>
                <Example>Tesla's market cap went from ~$80 billion in early 2020 to over $600 billion by year end — a 750% increase. That single year moved it from mid-cap to one of the world's most valuable companies, showing how rapidly a company can shift between categories when growth accelerates.</Example>
              </div>

              {/* Types of Stocks */}
              <div className="sf-card">
                <h3>Types of stocks</h3>
                <div className="row g-3">
                  <div className="col-md-6">
                    <strong>Common stock</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Provides voting rights. Most widely traded. Offers capital appreciation potential but lower priority on assets if a company fails.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Preferred stock</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Fixed dividends, higher claim on assets, usually no voting rights. Hybrid between common stock and bonds.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Growth stocks</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Companies expected to grow faster than average. Often trade at higher valuations and reinvest profits rather than pay dividends.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Value stocks</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Companies trading below intrinsic value. Often have lower P/E ratios and higher dividend yields.</p>
                  </div>
                </div>
              </div>

              {/* Market Psychology */}
              <div className="sf-card">
                <h3>Market sentiment & psychology</h3>
                <p>Short-term price movements are often driven more by emotion than fundamentals. Common biases to watch for:</p>
                <div className="row g-3">
                  <div className="col-md-6">
                    <strong>Herd mentality</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Following the crowd rather than doing independent analysis.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Loss aversion</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Feeling losses more intensely than equivalent gains — leading to holding losers too long.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Recency bias</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Overweighting recent events when predicting future performance.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Confirmation bias</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Only seeking information that confirms what you already believe.</p>
                  </div>
                </div>
                <Example>In March 2020, the VIX "Fear Index" hit an all-time high of 82.69 as COVID-19 fears peaked and the S&P 500 had fallen 34%. That extreme pessimism marked a buying opportunity — markets recovered all losses by August 2020, showing how contrarian thinking during periods of peak fear can be profitable.</Example>
              </div>

              {/* Key Takeaways */}
              <div className="sf-takeaways">
                <h3>Key takeaways</h3>
                <ul>
                  <li>The stock market is where pieces of companies are bought and sold between investors</li>
                  <li>Prices are set by supply and demand, driven by company performance, economic conditions, and sentiment</li>
                  <li>Stock exchanges provide organised, regulated places for trading</li>
                  <li>IPOs allow private companies to raise capital from the public for the first time</li>
                  <li>Market indices track segments of the market and serve as benchmarks</li>
                  <li>Markets cycle between bull (rising) and bear (falling) phases — understanding cycles helps you stay calm</li>
                  <li>Market cap (large, mid, small) describes a company's size and risk profile</li>
                  <li>Psychology drives short-term prices; fundamentals drive long-term value</li>
                </ul>
              </div>

              <div className="sf-next-page">
                <Link to="/investment-basics" className="btn-sf-primary">
                  Next: Investment Basics →
                </Link>
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StockMarketFundamentals;
